/**
 * Per-task sliding speed history for the task detail chart.
 *
 * Backend does not expose a per-task history endpoint, so we buffer samples
 * client-side: TasksView drives a 1s timer, pushes each active task's
 * download/upload speed into this store keyed by `gid`, and TaskSpeedChart
 * reads them back. Each task keeps exactly `windowSize` samples so the chart's
 * x-axis never stretches as data arrives.
 *
 * The store is module-level: every component that imports it sees the same
 * data, so opening the detail panel a few seconds after the timer started
 * still gets a full 60s window immediately.
 */
import { reactive, computed, type ComputedRef, type Ref } from 'vue'
import type { SpeedPoint } from './useSpeedHistory'

export interface UseTaskSpeedHistoryOptions {
  /** samples to retain per task (1 sample/s ⇒ 60 = 60s window) */
  windowSize?: number
  /** sampling period in ms; used when seeding/aligning the window */
  intervalMs?: number
}

export interface TaskSpeedHistoryApi {
  /** push one sample for `gid`; trims the oldest if over the window */
  addPoint(gid: string, downloadSpeed: number, uploadSpeed: number, ts?: number): void
  /** reactive copy of `gid`'s window (always `windowSize` long once seeded) */
  getPoints(gid: string): SpeedPoint[]
  /** forget one task's window (called when a task leaves the active list) */
  clear(gid: string): void
  /** forget every task's window */
  clearAll(): void
  /** run `cb` for every gid the store currently knows about */
  forEach(cb: (gid: string, points: SpeedPoint[]) => void): void
  /** how many distinct gids are tracked */
  size: number
}

const DEFAULTS = {
  windowSize: 60,
  intervalMs: 1000,
}

/**
 * Module-level reactive Map. Sharing a single instance across callers keeps
 * `getPoints()` reactive for every component that subscribes to it.
 *
 * `reactive(Map)` works with Vue 3 because the Map proxy fires `get`/`set`
 * traps that the reactivity system tracks.
 */
const windows = reactive<Map<string, SpeedPoint[]>>(new Map())

const cfg: Required<UseTaskSpeedHistoryOptions> = { ...DEFAULTS }

function seedWindow(now: number): SpeedPoint[] {
  const seed: SpeedPoint[] = []
  for (let i = cfg.windowSize - 1; i >= 0; i--) {
    seed.push({ ts: now - i * cfg.intervalMs, down: 0, up: 0 })
  }
  return seed
}

/**
 * Build the public API. The `use*` shape mirrors the rest of the project even
 * though we currently return a shared singleton; the factory exists so the
 * defaults can be re-applied from a caller if needed (and for future per-view
 * isolation).
 */
export function useTaskSpeedHistory(options: UseTaskSpeedHistoryOptions = {}): TaskSpeedHistoryApi {
  if (options.windowSize && options.windowSize !== cfg.windowSize) cfg.windowSize = options.windowSize
  if (options.intervalMs && options.intervalMs !== cfg.intervalMs) cfg.intervalMs = options.intervalMs

  function addPoint(gid: string, down: number, up: number, ts = Date.now()): void {
    const cur = windows.get(gid) ?? seedWindow(ts)
    const next = cur.slice()
    next.push({ ts, down: Math.max(0, Number(down) || 0), up: Math.max(0, Number(up) || 0) })
    while (next.length > cfg.windowSize) next.shift()
    windows.set(gid, next)
  }

  function getPoints(gid: string): SpeedPoint[] {
    return windows.get(gid) ?? []
  }

  function clear(gid: string): void {
    windows.delete(gid)
  }

  function clearAll(): void {
    windows.clear()
  }

  function forEach(cb: (gid: string, points: SpeedPoint[]) => void): void {
    windows.forEach((pts, gid) => cb(gid, pts))
  }

  return {
    addPoint,
    getPoints,
    clear,
    clearAll,
    forEach,
    get size() {
      return windows.size
    },
  }
}

/**
 * Reactive view of one task's window — handy in `<script setup>` where you want
 * a `ComputedRef<SpeedPoint[]>` to feed the chart directly.
 *
 * Usage:
 *   const points = useTaskSpeedPoints(() => props.gid)
 */
export function useTaskSpeedPoints(gid: () => string | null | undefined): ComputedRef<SpeedPoint[]> {
  return computed<SpeedPoint[]>(() => {
    const g = gid()
    if (!g) return []
    const pts = windows.get(g)
    return pts ?? []
  })
}

// re-export for convenience
export type { SpeedPoint }
export type TaskSpeedWindowRef = Ref<SpeedPoint[]>
